import { z } from "zod";

import type { DevParams } from "@/data/sync/url";

import { planSchema, sendBig, type BigUploadSettings } from "./big";
import { createLiveIssues } from "./issues-live";
import type { PutBlob } from "./put";
import {
  FilesError,
  dependentsSchema,
  fileSchema,
  folderListSchema,
  folderSchema,
  listSchema,
  trashListSchema,
  trashedFileSchema,
  type Converted,
  type DwgVersion,
  type FileUploadOptions,
  type FilesErrorCode,
  type ProjectFile,
  type ProjectFilesApi,
  type ProjectFolder,
  type Upload,
  type UploadTarget,
  tooLarge,
} from "./types";

/**
 * Project files on the doc service (`crates/services/doc/src/files/routes.rs`), on this origin: the gateway, and in
 * development Vite's proxy, sends `<apiPath>/*` there. The session cookie goes with every call; in dev builds the
 * page's `?dev-user=` and `?dev-org=` go too, as they do on the sync socket (RFC 0001 §6). DWG goes through the
 * service's conversion routes (`files/dwg/routes.rs`): the DXF form of a DWG, and a drawing's DXF saved as DWG. Delete,
 * the Trash, restore and delete forever go through its Trash routes (`files/trash/routes.rs`); images' and scanned PDF
 * pages' tile pyramids are on its raster routes (`files/raster/routes.rs`).
 */

/** The browser's fetch, or a fake in tests. Called as a plain function, never as a method. */
export type Fetch = (input: string, init: RequestInit) => Promise<Response>;

export interface LiveFilesOptions {
  /** Where the files API is, e.g. `/api/files`. */
  readonly apiPath: string;
  readonly fetch: Fetch;
  /** The dev sign-in the dev doc service trusts; none outside dev builds. */
  readonly dev: DevParams;
  /** The query parameters dev sign-in uses (`syncConfig`). */
  readonly devUserParam: string;
  readonly devOrgParam: string;
  /** The largest request body sent (`VITE_FILES_MAX_UPLOAD_BYTES`); larger ones are refused before they are sent. */
  readonly maxUploadBytes: number;
  /** The largest file `uploadFile` takes (`VITE_FILES_MAX_BIG_UPLOAD_BYTES`); the service has the last word. */
  readonly maxBigUploadBytes: number;
  /** How big uploads go to the storage: one PUT of bytes with progress, and how parts run and are retried. */
  readonly put: PutBlob;
  readonly bigUploads: BigUploadSettings;
  readonly sleep: (ms: number) => Promise<void>;
}

const API_VERSION = "v1";
const CODES: readonly FilesErrorCode[] = [
  "unauthenticated",
  "forbidden",
  "not_found",
  "gone",
  "bad_request",
  "too_large",
  "unreadable",
  "conflict",
  "upload_incomplete",
  "timed_out",
  "unavailable",
];
/** The header naming what a conversion left out: a JSON array of sentences, percent-encoded. */
const NOTES_HEADER = "x-orthos-conversion-notes";
const notesSchema = z.array(z.string());
const DXF_TYPE = "image/vnd.dxf";
const DWG_VERSION_PARAM = "dwgVersion";
const linkSchema = z.object({ url: z.string().min(1).nullable() });
const errorSchema = z.object({ error: z.string(), message: z.string() });
const NETWORK = "Orthos can't reach the project's files. Check your connection and try again.";
const UNEXPECTED = "The project's files sent something unexpected. Try again.";
/** HTTP 413, which a proxy may send before the service sees the upload. */
const PAYLOAD_TOO_LARGE = 413;

/** What a conversion left out, from its answer's header; none when the header is missing or unreadable. */
export function notesOf(response: Response): string[] {
  const header = response.headers.get(NOTES_HEADER);
  if (header === null) return [];
  try {
    const parsed = notesSchema.safeParse(JSON.parse(decodeURIComponent(header)));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

async function failure(response: Response): Promise<FilesError> {
  const parsed = errorSchema.safeParse(await response.json().catch(() => undefined));
  if (parsed.success) {
    const code = CODES.find((known) => known === parsed.data.error) ?? "unexpected";
    return new FilesError(code, parsed.data.message);
  }
  if (response.status === PAYLOAD_TOO_LARGE)
    return new FilesError("too_large", "The file is larger than the project's files take.");
  return new FilesError("unexpected", UNEXPECTED);
}

export function createLiveFilesApi(options: LiveFilesOptions): ProjectFilesApi {
  const url = (
    project: string,
    rest: string,
    query: Record<string, string> = {},
    collection = "files",
  ): string => {
    const params = new URLSearchParams(query);
    if (options.dev.user !== undefined) params.set(options.devUserParam, options.dev.user);
    if (options.dev.org !== undefined) params.set(options.devOrgParam, options.dev.org);
    const text = params.toString();
    const base = `${options.apiPath}/${API_VERSION}/projects/${encodeURIComponent(project)}/${collection}${rest}`;
    return text === "" ? base : `${base}?${text}`;
  };

  const drawingUrl = (drawingId: string, rest: string): string => {
    const params = new URLSearchParams();
    if (options.dev.user !== undefined) params.set(options.devUserParam, options.dev.user);
    if (options.dev.org !== undefined) params.set(options.devOrgParam, options.dev.org);
    const text = params.toString();
    const base = `${options.apiPath}/${API_VERSION}/drawings/${encodeURIComponent(drawingId)}${rest}`;
    return text === "" ? base : `${base}?${text}`;
  };

  const call = async (address: string, init: RequestInit): Promise<Response> => {
    let response: Response;
    try {
      response = await options.fetch(address, { credentials: "same-origin", ...init });
    } catch {
      throw new FilesError("network", NETWORK);
    }
    if (!response.ok) throw await failure(response);
    return response;
  };

  const file = async (response: Response): Promise<ProjectFile> => {
    const parsed = fileSchema.safeParse(await response.json().catch(() => undefined));
    if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
    return parsed.data;
  };

  const fits = (what: string, bytes: Uint8Array): void => {
    if (bytes.length > options.maxUploadBytes)
      throw tooLarge(what, bytes.length, options.maxUploadBytes);
  };

  const send = (upload: Upload): RequestInit => ({
    method: "POST",
    headers: { "Content-Type": upload.contentType, Accept: "application/json" },
    body: new Blob([upload.bytes as BlobPart], { type: upload.contentType }),
  });

  const sendDxf = (dxf: Uint8Array): RequestInit =>
    send({ bytes: dxf, contentType: DXF_TYPE, uploadedBy: "" });

  const jsonBody = (body: unknown): RequestInit => ({
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

  /** A picked file: through the service when it says small, else straight to the storage (`big.ts`). */
  const uploadFile = async (
    projectId: string,
    target: UploadTarget,
    picked: Blob,
    upload: FileUploadOptions,
  ): Promise<ProjectFile> => {
    if (picked.size > options.maxBigUploadBytes)
      throw tooLarge("The file", picked.size, options.maxBigUploadBytes);
    const asked = { ...target, size: picked.size, contentType: upload.contentType };
    const response = await call(url(projectId, "", {}, "uploads"), jsonBody(asked));
    const plan = planSchema.safeParse(await response.json().catch(() => undefined));
    if (!plan.success) throw new FilesError("unexpected", UNEXPECTED);
    if (plan.data.mode === "direct") {
      upload.onProgress?.(0, picked.size);
      const path = "fileId" in target ? `/${encodeURIComponent(target.fileId)}/versions` : "";
      const query = "name" in target ? { name: target.name } : {};
      const sent = await call(url(projectId, path, query), {
        method: "POST",
        headers: { "Content-Type": upload.contentType, Accept: "application/json" },
        body: picked,
      });
      upload.onProgress?.(picked.size, picked.size);
      return file(sent);
    }
    const uploads = (rest: string) => url(projectId, rest, {}, "uploads");
    const id = (uploadId: string) => `/${encodeURIComponent(uploadId)}`;
    const finished = await sendBig(
      plan.data,
      picked,
      { put: options.put, settings: options.bigUploads, sleep: options.sleep },
      {
        finish: (uploadId, parts) => call(uploads(`${id(uploadId)}/complete`), jsonBody({ parts })),
        abort: async (uploadId) => {
          await call(uploads(id(uploadId)), { method: "DELETE" }).catch(() => undefined);
        },
      },
      { onProgress: upload.onProgress, signal: upload.signal },
    );
    return file(finished);
  };

  const converted = async (response: Response): Promise<Converted> => ({
    bytes: new Uint8Array(await response.arrayBuffer()),
    notes: notesOf(response),
  });

  const folder = async (response: Response): Promise<ProjectFolder> => {
    const parsed = folderSchema.safeParse(await response.json().catch(() => undefined));
    if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
    return parsed.data;
  };

  const withMethod = (method: string, body: unknown): RequestInit => ({
    ...jsonBody(body),
    method,
  });
  const folderPath = (folderId: string) => `/${encodeURIComponent(folderId)}`;

  return {
    ...createLiveIssues(drawingUrl, call),
    async folders(projectId) {
      const response = await call(url(projectId, "", {}, "folders"), {
        headers: { Accept: "application/json" },
      });
      const parsed = folderListSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.folders;
    },
    async createFolder(projectId, name, parent) {
      return folder(await call(url(projectId, "", {}, "folders"), jsonBody({ name, parent })));
    },
    async updateFolder(projectId, folderId, change) {
      const address = url(projectId, folderPath(folderId), {}, "folders");
      return folder(await call(address, withMethod("PATCH", change)));
    },
    async deleteFolder(projectId, folderId) {
      await call(url(projectId, folderPath(folderId), {}, "folders"), { method: "DELETE" });
    },
    async moveFile(projectId, fileId, into) {
      const address = url(projectId, `/${encodeURIComponent(fileId)}`);
      return file(await call(address, withMethod("PATCH", { folder: into })));
    },
    async list(projectId) {
      const response = await call(url(projectId, ""), { headers: { Accept: "application/json" } });
      const parsed = listSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.files;
    },
    async upload(projectId, name, upload) {
      fits(name, upload.bytes);
      return file(await call(url(projectId, "", { name }), send(upload)));
    },
    async addVersion(projectId, fileId, upload) {
      fits("The new version", upload.bytes);
      const path = `/${encodeURIComponent(fileId)}/versions`;
      const save = upload.save;
      const query =
        save === undefined
          ? {}
          : { session: save.session, ...(save.from === undefined ? {} : { from: save.from }) };
      return file(await call(url(projectId, path, query), send(upload)));
    },
    uploadFile,
    async downloadLink(projectId, fileId, version) {
      const query = version === undefined ? {} : { version: String(version) };
      const response = await call(url(projectId, `/${encodeURIComponent(fileId)}/link`, query), {
        headers: { Accept: "application/json" },
      });
      const parsed = linkSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.url ?? undefined;
    },
    rasterEndpoint(projectId, fileId, version, page) {
      const query = { version: String(version), page: String(page) };
      const at = (rest: string) =>
        url(projectId, `/${encodeURIComponent(fileId)}/raster${rest}`, query);
      return {
        manifestUrl: at(""),
        pageUrl: at("/page"),
        tileUrl: (level, col, row) => at(`/${String(level)}/${String(col)}/${String(row)}`),
        sheetTilesUrl: url(projectId, `/${encodeURIComponent(fileId)}/sheet-tiles`, query),
      };
    },
    async dxfOf(projectId, fileId, version) {
      const query = version === undefined ? {} : { version: String(version) };
      const response = await call(url(projectId, `/${encodeURIComponent(fileId)}/dxf`, query), {});
      return converted(response);
    },
    async saveDwg(projectId, name, dxf, version) {
      fits("The drawing's DXF", dxf);
      const query = { name, [DWG_VERSION_PARAM]: version };
      const response = await call(url(projectId, "/as-dwg", query), sendDxf(dxf));
      const notes = notesOf(response);
      return { file: await file(response), notes };
    },
    async toDwg(projectId, dxf, version: DwgVersion) {
      fits("The drawing's DXF", dxf);
      const query = { [DWG_VERSION_PARAM]: version };
      return converted(await call(url(projectId, "/to-dwg", query), sendDxf(dxf)));
    },
    async remove(projectId, fileId) {
      const response = await call(url(projectId, `/${encodeURIComponent(fileId)}`), {
        method: "DELETE",
        headers: { Accept: "application/json" },
      });
      const parsed = trashedFileSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data;
    },
    async trash(projectId) {
      const response = await call(url(projectId, "", {}, "trash"), {
        headers: { Accept: "application/json" },
      });
      const parsed = trashListSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.files;
    },
    async restore(projectId, fileId) {
      const path = `/${encodeURIComponent(fileId)}/restore`;
      return file(
        await call(url(projectId, path, {}, "trash"), {
          method: "POST",
          headers: { Accept: "application/json" },
        }),
      );
    },
    async dependents(projectId, fileId) {
      const path = `/${encodeURIComponent(fileId)}/dependents`;
      const response = await call(url(projectId, path), {
        headers: { Accept: "application/json" },
      });
      const parsed = dependentsSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.drawings;
    },
    async purge(projectId, fileId) {
      await call(url(projectId, `/${encodeURIComponent(fileId)}`, {}, "trash"), {
        method: "DELETE",
      });
    },
    async download(projectId, fileId, version) {
      const query = version === undefined ? {} : { version: String(version) };
      const response = await call(
        url(projectId, `/${encodeURIComponent(fileId)}/content`, query),
        {},
      );
      return new Uint8Array(await response.arrayBuffer());
    },
  };
}
